/* eslint-disable sonarjs/cognitive-complexity */

'use client';

import { motion } from 'framer-motion';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import type { MouseEvent } from 'react';
import { useInViewScroll, useSanitizedTranslation, XumoButton, StackedContent } from 'ui';
import { useWindowSize } from '@uidotdev/usehooks';
import Image from 'next/image';
import { Sweepstake } from '../Sweepstake';
import Disclaimer from '../Disclaimer';

type SweepstakeItem = {
  id: string;
  image?: { src: string; alt: string };
  mobileImage?: { src: string; alt: string };
  logo?: { src: string; alt: string };
  headingBold?: string;
  body?: string;
  button?: { label: string; href: string };
  availability?: { appStore?: boolean; items?: { store: string; link: string }[] };
  successMessage?: string;
  startDate: string;
  endDate: string;
  rulesLink?: string;
  hideWhenSweepstakeEnded?: boolean;
  tabTitle?: string;
  tabLabel?: string;
  disclaimer?: string;
  cardUI?: {
    variant?: 'event' | 'tv' | 'fullImage';
    title?: {
      thin?: string;
      bold?: string;
    };
    description?: string;
    meta?: string;
    backgroundImage?: { src: string; alt: string };
    contentImage?: { src: string; alt: string };
    shallowContentImage?: boolean;
    accentImage?: { src: string; alt: string };
    accentImageAlign?: 'rightCenter' | 'leftCenter' | 'topRight';
    accentImageWidthPx?: number;
    backgroundBlur?: {
      enabled?: boolean;
      amountPx?: number;
    };
    backgroundTint?: {
      enabled?: boolean;
      color?: string;
      opacity?: number;
    };
    blurHalf?: {
      enabled?: boolean;
      side?: 'top' | 'bottom';
    };
    cta?: {
      label?: string;
      variant?: 'light' | 'berry';
    };
    disclaimer?: string;
    logoBelowContent?: boolean;
  };
};

type SweepstakesGalleryAltProps = {
  stackedContent?: any[];
  sweepstakes: SweepstakeItem[];
  desktopVerticalCards?: boolean;
  desktopVerticalHeadingSize?: 'default' | 'sm' | 'xs';
  hideTabListOnMobile?: boolean;
  openFormOnHashSelect?: boolean;
};

const desktopVerticalHeadingSizeMap: Record<NonNullable<SweepstakesGalleryAltProps['desktopVerticalHeadingSize']>, string> = {
  default: 'type-card-heading-bold',
  sm: 'font-WorkSans text-3xl md:text-[52px] leading-[0.95] tracking-[-2px] lg:text-[60px] font-medium',
  xs: 'font-WorkSans text-2xl md:text-[42px] leading-[0.95] tracking-[-1.5px] lg:text-[45px] font-medium',
};

const shouldShowSweepstake = (item: SweepstakeItem) => {
  const now = new Date();
  const startDate = new Date(item.startDate);
  const endDate = new Date(item.endDate);

  // Never render before the sweepstake has started or after it has ended.
  return now >= startDate && now <= endDate;
};

const getTabLabel = (item: SweepstakeItem) => {
  if (item.tabTitle) {
    return item.tabTitle;
  }

  if (item.tabLabel) {
    return item.tabLabel;
  }

  if (item.headingBold) {
    return item.headingBold
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }

  return item.id;
};

const sweepstakesHashValue = (id: string) => id;

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const animateValue = (from: number, to: number, durationMs: number, onUpdate: (value: number) => void, onComplete?: () => void) => {
  const start = performance.now();
  const distance = to - from;
  let frameId = 0;

  const step = (now: number) => {
    const elapsed = now - start;
    const progress = Math.min(1, elapsed / durationMs);
    const eased = easeInOutCubic(progress);
    onUpdate(from + distance * eased);

    if (progress < 1) {
      frameId = requestAnimationFrame(step);
      return;
    }

    onComplete?.();
  };

  frameId = requestAnimationFrame(step);

  return () => {
    cancelAnimationFrame(frameId);
  };
};

const SweepstakesGalleryItem = ({ sweepstake, onEnterNow }: { sweepstake: SweepstakeItem; onEnterNow: () => void }) => {
  const { t } = useSanitizedTranslation();

  const logoMap: Record<string, string> = {
    amazon: '/static/images/refresh/2025/logos/amazon.svg',
    bestBuy: '/static/images/refresh/2025/logos/bestbuy.svg',
    walmart: '/static/images/refresh/2025/logos/walmart.svg',
    xfinity: '/static/images/refresh/2025/logos/xfinity.svg',
    spectrum: '/static/images/refresh/2025/logos/spectrum.svg',
  };

  const heading = sweepstake.headingBold ?? '';

  return (
    <div className="relative flex aspect-[1471/650] origin-top overflow-clip rounded-2xl lg:w-[1050px] xl:aspect-[1471/650] xl:w-[1250px] 2xl:aspect-[1471/620] 2xl:w-[1350px]">
      {sweepstake.image?.src ? (
        <Image src={sweepstake.image.src} alt={sweepstake.image.alt} priority fill sizes="100vw" className="top-0 h-[100%] w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-l from-[#5B2E4F] to-[#150E14]" />
      )}

      <div className="absolute inset-0 block h-full w-full bg-gradient-to-r from-[#5B2E4F] from-[33%] to-transparent xl:hidden" />

      <div className="z-50 flex h-full w-full flex-col justify-between px-12 py-10 text-white lg:w-2/3 xl:w-1/2 xl:px-14 xl:py-12">
        {sweepstake.logo?.src ? <Image src={sweepstake.logo.src} alt={sweepstake.logo.alt} width={210} height={40} /> : <div />}

        <h3 className="type-card-heading-bold" data-i18n dangerouslySetInnerHTML={{ __html: t(heading) }} />
        <p className="type-card-small w-[90%]" data-i18n dangerouslySetInnerHTML={{ __html: t(sweepstake.body ?? '') }} />

        <div>
          <XumoButton
            primary
            light
            label="Enter now"
            href="#"
            submit={undefined}
            children={undefined}
            target={undefined}
            disabled={undefined}
            alignment={undefined}
            secondary={undefined}
            color={undefined}
            className={undefined}
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              onEnterNow();
            }}
          />
        </div>

        <div className="flex">
          {sweepstake.availability?.appStore ? (
            <div className="flex items-center gap-4">
              <a href="https://play.google.com/store/apps/details?id=com.xumo.xumo" target="_blank" rel="noopener noreferrer">
                <Image src="/static/images/refresh/google-play.png" alt="Get it on Google Play" width={159} height={59} />
              </a>
              <a href="https://apps.apple.com/us/app/xumo-play-stream-tv-movies/id1068337978" target="_blank" rel="noopener noreferrer">
                <Image src="/static/images/refresh/apple-app-store.png" alt="Download on the App Store" width={139} height={59} />
              </a>
            </div>
          ) : (
            <div className="flex items-center gap-6">
              {sweepstake.availability?.items?.map((item, idx) => {
                const bestBuy = item.store === 'bestBuy';

                return (
                  <a key={'sweepstakeAvailabilityLogo' + idx} href={item.link} target="_blank" rel="noopener noreferrer">
                    <Image src={logoMap[item.store]} width={0} height={0} alt={item.store} className={`${bestBuy ? 'h-[40px]' : 'h-[30px]'} w-auto`} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MobileSweepstakesGalleryItem = ({
  sweepstake,
  onEnterNow,
  headingClassName = 'type-card-heading-bold',
}: {
  sweepstake: SweepstakeItem;
  onEnterNow: () => void;
  headingClassName?: string;
}) => {
  const { t } = useSanitizedTranslation();
  const cardVariant = sweepstake.cardUI?.variant ?? 'event';
  const displayImage = sweepstake.cardUI?.backgroundImage || sweepstake.mobileImage || sweepstake.image;
  const headingBold = sweepstake.cardUI?.title?.bold ?? sweepstake.headingBold ?? '';
  const headingThin = sweepstake.cardUI?.title?.thin;
  const description = sweepstake.cardUI?.description;
  const meta = sweepstake.cardUI?.meta ?? sweepstake.body ?? '';
  const ctaLabel = sweepstake.cardUI?.cta?.label ?? sweepstake.button?.label ?? 'Enter now';
  const berryButton = sweepstake.cardUI?.cta?.variant === 'berry';
  const blurHalfEnabled = sweepstake.cardUI?.blurHalf?.enabled;
  const blurHalfSide = sweepstake.cardUI?.blurHalf?.side ?? 'bottom';
  const blurBackgroundImage = sweepstake.cardUI?.backgroundBlur?.enabled;
  const backgroundBlurAmountPx = sweepstake.cardUI?.backgroundBlur?.amountPx ?? 8;
  const backgroundTintEnabled = sweepstake.cardUI?.backgroundTint?.enabled;
  const backgroundTintColor = sweepstake.cardUI?.backgroundTint?.color ?? '#4bb5c4';
  const backgroundTintOpacity = sweepstake.cardUI?.backgroundTint?.opacity ?? 0.22;
  const accentImage = sweepstake.cardUI?.accentImage;
  const accentImageAlign = sweepstake.cardUI?.accentImageAlign ?? 'rightCenter';
  const accentImageWidthPx = sweepstake.cardUI?.accentImageWidthPx ?? 230;
  const hasContentImage = Boolean(sweepstake.cardUI?.contentImage?.src);
  const logoBelowContent = sweepstake.cardUI?.logoBelowContent ?? false;
  const shallowContentImage = sweepstake.cardUI?.shallowContentImage ?? false;

  const renderCTA = () => (
    <XumoButton
      primary
      light={!berryButton}
      label={ctaLabel}
      href="#"
      submit={undefined}
      children={undefined}
      target={undefined}
      disabled={undefined}
      alignment={undefined}
      secondary={undefined}
      color={undefined}
      className={['py:1.5 px-7 md:py-2.5', berryButton ? 'bg-xumoBerry border-xumoBerry hover:outline-xumoBerry text-white' : ''].join(' ')}
      onClick={(e: MouseEvent) => {
        e.preventDefault();
        onEnterNow();
      }}
    />
  );

  if (cardVariant === 'fullImage') {
    return (
      <div className="relative grid h-[720px] w-[85vw] max-w-[550px] grid-cols-1 grid-rows-1 items-center justify-items-center overflow-hidden rounded-2xl lg:h-[760px]">
        {/* Content */}
        <div className="z-10 col-start-1 row-start-1 flex h-full w-full flex-col items-center gap-10 py-8 text-center text-white">
          {sweepstake.logo?.src && !logoBelowContent && (
            <Image src={sweepstake.logo.src} alt={sweepstake.logo.alt ?? ''} width={130} height={20} className="scale-90 sm:scale-100" />
          )}

          {sweepstake.cardUI?.contentImage?.src && (
            <div className="w-full">
              <Image
                src={sweepstake.cardUI.contentImage.src}
                alt={sweepstake.cardUI.contentImage.alt}
                width={1100}
                height={550}
                sizes="(max-width: 648px) 85vw, 550px"
                className={`${logoBelowContent ? 'mt-12' : '-mt-4'} mx-auto h-auto object-contain md:-mt-0 ${shallowContentImage ? 'w-4/5' : 'w-full'}`}
              />
            </div>
          )}

          {sweepstake.logo?.src && logoBelowContent && (
            <Image
              src={sweepstake.logo.src}
              alt={sweepstake.logo.alt ?? ''}
              width={130}
              height={20}
              className={`md:-mt-26 relative z-10 -mt-16 scale-90 min-[450px]:-mt-24 sm:-mt-28 sm:scale-100`}
            />
          )}

          <div
            className={[
              'flex w-full flex-col items-center gap-3 px-10 sm:px-24 md:px-20 lg:px-5',
              logoBelowContent ? '' : ` min-[450px]:-mt-24 ${shallowContentImage ? '-mt-6 sm:-mt-10' : '-mt-20 sm:-mt-28'} `,
            ].join(' ')}
          >
            {headingThin && (
              <p
                className="font-WorkSans text-white/85 text-[25px] font-light md:text-[30px] lg:text-[40px]"
                data-i18n
                dangerouslySetInnerHTML={{ __html: t(headingThin) }}
              />
            )}
            <h3
              className={[headingClassName, 'weight-500 -mt-4 mb-4 text-[34px] md:w-4/5 lg:text-[34px]'].join(' ')}
              data-i18n
              dangerouslySetInnerHTML={{ __html: t(headingBold) }}
            />
            <div className="h-px w-[72%] bg-white" />
            <p className="type-body-base weight-600 mt-4  uppercase tracking-[1.2px]" data-i18n dangerouslySetInnerHTML={{ __html: t(meta) }} />
            <div className="mt-4">{renderCTA()}</div>
          </div>
        </div>

        {/* Background */}
        <div className="z-0 col-start-1 row-start-1 flex h-full w-full flex-col">
          {displayImage?.src ? (
            <Image
              src={displayImage.src}
              alt={displayImage.alt}
              fill
              className="object-cover"
              style={blurBackgroundImage ? { filter: `blur(${backgroundBlurAmountPx}px)` } : undefined}
            />
          ) : (
            <div className="absolute inset-0 bg-[#2D1A2A]">
              <div className="absolute inset-x-0 top-0 h-[66%] bg-gradient-to-b from-[rgba(91,46,79,0.95)] via-[rgba(59,33,57,0.82)] to-transparent" />
            </div>
          )}

          {displayImage?.src && backgroundTintEnabled && (
            <div
              className="pointer-events-none absolute inset-0 z-10"
              style={{
                backgroundColor: backgroundTintColor,
                opacity: backgroundTintOpacity,
              }}
            />
          )}

          {displayImage?.src && blurHalfEnabled && (
            <div
              className={['absolute inset-x-0 z-20 backdrop-blur-md', blurHalfSide === 'top' ? 'bg-black/35 top-0 h-1/2' : 'bg-black/35 bottom-0 h-1/2'].join(
                ' ',
              )}
            />
          )}

          {displayImage?.src && <div className="to-black/45 absolute inset-0 z-20 bg-gradient-to-b from-black/30 via-transparent" />}

          {accentImage?.src && (
            <div
              className={[
                'pointer-events-none absolute z-20',
                accentImageAlign === 'rightCenter'
                  ? 'right-[-95px] top-1/2 -translate-y-1/2'
                  : accentImageAlign === 'leftCenter'
                  ? 'left-[-95px] top-1/2 -translate-y-1/2'
                  : 'right-[-70px] top-16',
              ].join(' ')}
              style={{ width: `${accentImageWidthPx}px` }}
            >
              <Image
                src={accentImage.src}
                alt={accentImage.alt}
                width={accentImageWidthPx}
                height={accentImageWidthPx}
                className="h-auto w-full object-contain"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (cardVariant === 'event') {
    return (
      <div className="relative flex h-[720px] w-[85vw] max-w-[550px] flex-col overflow-hidden rounded-2xl bg-[#2D1A2A] lg:h-[760px]">
        {displayImage?.src && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden">
            <img src={displayImage.src} alt={displayImage.alt} className="block h-auto w-full" />
          </div>
        )}

        <div className="pointer-events-none absolute inset-x-0 top-0 h-[66%] bg-gradient-to-b from-[rgba(91,46,79,0.95)] via-[rgba(59,33,57,0.82)] to-transparent" />

        <div className="relative z-10 flex h-full min-h-0 w-full flex-col items-center px-6 py-8 text-center text-white lg:px-8 lg:py-10">
          {sweepstake.logo?.src && <Image src={sweepstake.logo.src} alt={sweepstake.logo.alt ?? ''} width={130} height={20} />}

          <div className="mt-5 flex w-full flex-col items-center">
            {headingThin && (
              <p
                className="font-WorkSans text-white/85 text-[24px] font-extralight md:text-[28px]"
                data-i18n
                dangerouslySetInnerHTML={{ __html: t(headingThin) }}
              />
            )}

            <h3 className={`${headingClassName} mt-2 font-bold`} data-i18n dangerouslySetInnerHTML={{ __html: t(headingBold) }} />

            {description && (
              <p
                className="weight-300  mt-4 max-w-[90%] text-[16px] text-white/90 md:text-[20px]"
                data-i18n
                dangerouslySetInnerHTML={{ __html: t(description) }}
              />
            )}

            <div className="mt-5 h-px w-[72%] bg-white" />

            <p className="type-body-base weight-600 mt-5 uppercase tracking-[1.2px]" data-i18n dangerouslySetInnerHTML={{ __html: t(meta) }} />

            <div className="mt-6">{renderCTA()}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-[720px] w-[85vw] max-w-[550px] flex-col overflow-hidden rounded-2xl bg-[#2D1A2A] lg:h-[760px]">
      {displayImage?.src && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden">
          <img src={displayImage.src} alt={displayImage.alt} className="block h-auto w-full" />
        </div>
      )}

      <div className="pointer-events-none absolute inset-x-0 top-0 h-[66%] bg-gradient-to-b from-[rgba(91,46,79,0.95)] via-[rgba(59,33,57,0.82)] to-transparent" />

      <div className="relative z-10 flex h-full min-h-0 w-full flex-col items-center px-4 py-8 text-center text-white">
        {sweepstake.logo?.src && <Image src={sweepstake.logo.src} alt={sweepstake.logo.alt ?? ''} width={130} height={20} />}

        <div className="mt-8 flex w-full flex-col items-center gap-3">
          {headingThin && (
            <p
              className="font-WorkSans text-white/85 text-[40px] font-extralight leading-none tracking-tight lg:w-4/5"
              data-i18n
              dangerouslySetInnerHTML={{ __html: t(headingThin) }}
            />
          )}
          <h3 className={headingClassName} data-i18n dangerouslySetInnerHTML={{ __html: t(headingBold) }} />

          <div className="mt-6">{renderCTA()}</div>
        </div>
      </div>
    </div>
  );
};

const SweepstakesGalleryAlt = ({
  stackedContent = [],
  sweepstakes,
  desktopVerticalCards = false,
  desktopVerticalHeadingSize = 'default',
  hideTabListOnMobile = false,
  openFormOnHashSelect = false,
}: SweepstakesGalleryAltProps) => {
  const { ref } = useInViewScroll([0, 1], ['start 100vh', 'end 0vh']);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [pendingFormScroll, setPendingFormScroll] = useState(false);
  const visibleSweepstakes = useMemo(() => sweepstakes.filter(shouldShowSweepstake), [sweepstakes]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!new URLSearchParams(window.location.search).has('debugSweepstakes')) return;

    const now = new Date();
    // eslint-disable-next-line no-console
    console.table(
      sweepstakes.map(item => {
        const startDate = new Date(item.startDate);
        const endDate = new Date(item.endDate);
        let status = 'active';
        if (now < startDate) status = 'upcoming';
        else if (now > endDate) status = 'ended';

        return {
          id: item.id,
          tab: getTabLabel(item),
          startDate: item.startDate,
          endDate: item.endDate,
          startET: startDate.toLocaleString('en-US', { timeZone: 'America/New_York' }),
          endET: endDate.toLocaleString('en-US', { timeZone: 'America/New_York' }),
          status,
          visible: shouldShowSweepstake(item),
        };
      }),
    );
  }, [sweepstakes]);

  const tabListRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const cancelHorizontalAnimationRef = useRef<() => void>(() => {});
  const cancelVerticalAnimationRef = useRef<() => void>(() => {});
  const [offsets, setOffsets] = useState<number[]>([]);

  const { width: windowWidth } = useWindowSize();
  const [isMobile, setIsMobile] = useState(false);
  const useVerticalCards = isMobile || desktopVerticalCards;
  const verticalHeadingClassName = !isMobile && desktopVerticalCards ? desktopVerticalHeadingSizeMap[desktopVerticalHeadingSize] : 'type-card-heading-bold';

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 1100);
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, []);

  const scrollToIndex = useCallback(
    (index: number) => {
      const el = containerRef.current;
      const card = cardRefs.current[index];
      if (!el || !card || !isMobile) return;

      setIsUserScrolling(true);

      const cardLeft = card.offsetLeft;
      const cardWidth = card.offsetWidth;
      const containerWidth = el.clientWidth;
      const targetScroll = cardLeft - (containerWidth - cardWidth) / 2;

      cancelHorizontalAnimationRef.current();
      cancelHorizontalAnimationRef.current = animateValue(el.scrollLeft, Math.max(0, targetScroll), 900, nextLeft => {
        el.scrollLeft = nextLeft;
      });

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      // Keep index updates paused while the slower card glide is running.
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 950);
    },
    [isMobile],
  );

  const handleSelect = (index: number, options?: { syncMobileCarousel?: boolean }) => {
    setCurrentIndex(index);
    if (isMobile && options?.syncMobileCarousel !== false) {
      scrollToIndex(index);
    }
  };

  const getSweepstakeIndexFromHash = useCallback(
    (hash: string) => {
      const rawHash = decodeURIComponent(hash.replace(/^#/, ''));
      const id = rawHash.startsWith('sweepstakes-tab-') ? rawHash.replace(/^sweepstakes-tab-/, '') : rawHash;
      return visibleSweepstakes.findIndex(item => item.id === id);
    },
    [visibleSweepstakes],
  );

  const updateHash = (id: string) => {
    if (typeof window === 'undefined') {
      return;
    }
    window.history.replaceState(null, '', `#${encodeURIComponent(sweepstakesHashValue(id))}`);
  };

  const scrollTabListIntoView = useCallback(() => {
    if (!tabListRef.current) {
      return;
    }

    const headerOffset = 100;
    const top = tabListRef.current.getBoundingClientRect().top + window.scrollY - headerOffset;

    const targetTop = Math.max(0, top);
    cancelVerticalAnimationRef.current();
    cancelVerticalAnimationRef.current = animateValue(window.scrollY, targetTop, 850, nextTop => {
      window.scrollTo(0, nextTop);
    });
  }, []);

  const scrollCardTopIntoView = useCallback((index: number) => {
    const card = cardRefs.current[index];
    if (!card) {
      return;
    }

    const headerOffset = 100;
    const top = card.getBoundingClientRect().top + window.scrollY - headerOffset;

    const targetTop = Math.max(0, top);
    cancelVerticalAnimationRef.current();
    cancelVerticalAnimationRef.current = animateValue(window.scrollY, targetTop, 900, nextTop => {
      window.scrollTo(0, nextTop);
    });
  }, []);

  useEffect(() => {
    if (!isMobile || !containerRef.current) return;

    const el = containerRef.current;
    let rafId = 0;

    const updateCurrentIndex = () => {
      if (isUserScrolling) return;

      const scrollLeft = el.scrollLeft;
      const containerWidth = el.clientWidth;
      const scrollCenter = scrollLeft + containerWidth / 2;

      let closestIndex = 0;
      let minDistance = Infinity;

      cardRefs.current.forEach((card, index) => {
        if (!card) return;

        const cardLeft = card.offsetLeft;
        const cardWidth = card.offsetWidth;
        const cardCenter = cardLeft + cardWidth / 2;
        const distance = Math.abs(scrollCenter - cardCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== currentIndex) {
        setCurrentIndex(closestIndex);
      }
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateCurrentIndex);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    updateCurrentIndex();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [isMobile, currentIndex, isUserScrolling]);

  useEffect(() => {
    if (isMobile || !containerRef.current || !cardRefs.current.length) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    const newOffsets = cardRefs.current.map(card => {
      if (!card) return 0;
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      return containerCenter - cardCenter;
    });

    setOffsets(newOffsets);
  }, [visibleSweepstakes.length, windowWidth, isMobile]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      cancelHorizontalAnimationRef.current();
      cancelVerticalAnimationRef.current();
    };
  }, []);

  useEffect(() => {
    if (currentIndex > visibleSweepstakes.length - 1) {
      setCurrentIndex(0);
    }
  }, [currentIndex, visibleSweepstakes.length]);

  const handleEnterNow = (index: number) => {
    setCurrentIndex(index);
    setShowForm(true);
    setPendingFormScroll(true);
    updateHash(visibleSweepstakes[index].id);
    if (isMobile) {
      scrollToIndex(index);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !visibleSweepstakes.length) {
      return;
    }

    const syncFromHash = () => {
      const hash = window.location.hash;
      if (!hash) {
        return;
      }

      const matchedIndex = getSweepstakeIndexFromHash(hash);
      if (matchedIndex === -1) {
        return;
      }

      setCurrentIndex(matchedIndex);
      if (openFormOnHashSelect) {
        setShowForm(true);
        setPendingFormScroll(true);
      } else {
        requestAnimationFrame(() => {
          if (useVerticalCards) {
            scrollCardTopIntoView(matchedIndex);
            return;
          }

          scrollTabListIntoView();
        });
      }
      if (isMobile) {
        scrollToIndex(matchedIndex);
      }
    };

    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);

    return () => {
      window.removeEventListener('hashchange', syncFromHash);
    };
  }, [
    visibleSweepstakes,
    getSweepstakeIndexFromHash,
    isMobile,
    useVerticalCards,
    openFormOnHashSelect,
    scrollToIndex,
    scrollTabListIntoView,
    scrollCardTopIntoView,
  ]);

  useEffect(() => {
    if (!pendingFormScroll || !showForm || !formContainerRef.current) {
      return;
    }

    const top = formContainerRef.current.getBoundingClientRect().top + window.scrollY;
    cancelVerticalAnimationRef.current();
    cancelVerticalAnimationRef.current = animateValue(window.scrollY, top, 850, nextTop => {
      window.scrollTo(0, nextTop);
    });
    setPendingFormScroll(false);
  }, [pendingFormScroll, showForm, currentIndex]);

  useEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, visibleSweepstakes.length);
  }, [visibleSweepstakes.length]);

  return (
    <motion.section ref={ref} className="relative z-40 flex w-full flex-col justify-center bg-white pb-16 pt-0" data-testid={'sweepstakes-gallery-alt'}>
      <div className="wrapper text-xumoDarkPlum">
        <StackedContent
          full={false}
          fullalways
          fullheight
          mobileCenter={false}
          mobileCenterLg
          center
          light={false}
          noOrphans={false}
          space
          minHeight={false}
          left={false}
          noMargin={false}
          margin="0px 0px 0px 0px"
          content={stackedContent}
          className={'text-xumoDarkPlum'}
        />
      </div>

      {visibleSweepstakes.length > 1 && !(hideTabListOnMobile && isMobile) && (
        <div ref={tabListRef} className="mx-auto mb-6 mt-8 flex justify-center px-4 md:mt-16 xl:mb-8">
          <div className="flex flex-col items-center gap-3 md:flex-row md:gap-12">
            {visibleSweepstakes.map((item, index) => (
              <div
                key={'sweepstakesGalleryAltButton' + item.id}
                className={`rounded-full ${currentIndex === index ? 'to-xumoDarkPlum bg-gradient-to-b from-[#945B87] via-[#66345B] p-[2px]' : 'bg-white'}`}
              >
                <a
                  href={`#${encodeURIComponent(sweepstakesHashValue(item.id))}`}
                  onClick={e => {
                    e.preventDefault();
                    handleSelect(index, { syncMobileCarousel: false });
                    scrollTabListIntoView();
                  }}
                  className={`weight-500 rounded-full text-lg tracking-[-1px] md:text-[24px] md:tracking-[-1.5px] ${
                    currentIndex === index ? 'text-xumoDarkPlum bg-white px-4 py-1 md:px-6' : 'text-xumoDarkPlum/50 bg-white px-4 py-1 md:px-6'
                  }`}
                >
                  {getTabLabel(item)}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {visibleSweepstakes.length > 0 && (
        <div className="my-4 flex items-center justify-center md:my-8">
          <motion.div
            ref={containerRef}
            className={[
              'flex w-full items-center',
              isMobile ? 'scrollbar-hide touch-auto snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain px-4' : 'gap-8',
            ].join(' ')}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
            animate={isMobile ? {} : { x: offsets[currentIndex] || 0 }}
            transition={{
              type: 'spring',
              stiffness: 250,
              damping: 50,
              duration: 0.6,
            }}
          >
            {visibleSweepstakes.map((item, index) => (
              <div
                key={item.id ?? index}
                ref={el => {
                  cardRefs.current[index] = el;
                }}
                className={['scroll-mt-24 transition-all duration-300', isMobile ? 'shrink-0 snap-center last:mr-4' : 'mr-8 last:mr-0'].join(' ')}
                style={
                  isMobile
                    ? {
                        scrollSnapAlign: 'center',
                        scrollSnapStop: 'always',
                      }
                    : {}
                }
              >
                {useVerticalCards ? (
                  <MobileSweepstakesGalleryItem sweepstake={item} onEnterNow={() => handleEnterNow(index)} headingClassName={verticalHeadingClassName} />
                ) : (
                  <SweepstakesGalleryItem sweepstake={item} onEnterNow={() => handleEnterNow(index)} />
                )}
              </div>
            ))}
          </motion.div>
        </div>
      )}

      {visibleSweepstakes.length === 0 && (
        <div className="my-4 flex items-center justify-center md:my-8">
          <div className="w-full max-w-[1200px]">
            <Sweepstake
              id="no-active-sweepstakes"
              startDate="2999-01-01T00:00:00Z"
              endDate="2999-01-02T00:00:00Z"
              hideWhenSweepstakeEnded={false}
              hideGradient
            />
          </div>
        </div>
      )}

      {visibleSweepstakes.length > 1 && (
        <div className="mb-4 mt-4 flex justify-center gap-2 lg:mt-0">
          {visibleSweepstakes.map((_, index) => (
            <button
              key={index}
              onClick={() => handleSelect(index)}
              className={`h-3 w-3 rounded-full transition-all duration-300 ${currentIndex === index ? 'bg-xumoDarkPlum w-8' : 'bg-xumoDarkPlum/30'}`}
              aria-label={`Go to sweepstake ${index + 1}`}
            />
          ))}
        </div>
      )}

      {showForm && visibleSweepstakes[currentIndex] && (
        <div ref={formContainerRef} className="mt-8">
          <Sweepstake
            id={visibleSweepstakes[currentIndex].id}
            headingBold={visibleSweepstakes[currentIndex].headingBold}
            body={visibleSweepstakes[currentIndex].body}
            successMessage={visibleSweepstakes[currentIndex].successMessage}
            startDate={visibleSweepstakes[currentIndex].startDate}
            endDate={visibleSweepstakes[currentIndex].endDate}
            rulesLink={visibleSweepstakes[currentIndex].rulesLink}
            hideWhenSweepstakeEnded={visibleSweepstakes[currentIndex].hideWhenSweepstakeEnded}
            hideGradient
          />
          {/* Disclaimers swap out */}
          <Disclaimer
            disclaimer={
              `<p>This a secure website, and all information collected is confidential. We make every effort to use the latest technology to ensure the security of your personal information. ​</p><br/>` +
              visibleSweepstakes[currentIndex].disclaimer
            }
          />
          {/* <p className="mx-auto px-16 text-center text-sm leading-tight" dangerouslySetInnerHTML={{ __html: visibleSweepstakes[currentIndex].disclaimer }} /> */}
        </div>
      )}
    </motion.section>
  );
};

export default SweepstakesGalleryAlt;
